import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { PageHeader, Card, SectionTitle, StatCard, StatusPill, Money } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import PeriodPicker, { DeltaPill } from "../components/ui-bj/PeriodPicker";
import { useAirtable } from "../hooks/useAirtable";
import { money, str, fmtDate, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import { relabel } from "../lib/displayNames";

const tooltipStyle = {
  background: "#0E1C15",
  border: "1px solid rgba(200,164,106,0.3)",
  borderRadius: 0,
  fontFamily: "Hanken Grotesk, monospace",
  fontSize: 12,
  color: "#2A1C15",
};

const MN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const prevKey = (key) => {
  const [y, m] = key.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
};

const monthOf = (s) => (typeof s === "string" ? s.slice(0, 7) : "");

const periodLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return `${MN[m - 1]} ${y}`;
};

const RevenuePage = () => {
  const invoices = useAirtable("invoices");
  const clients = useAirtable("clients");
  const clientMap = useMemo(() => indexById(clients.records), [clients.records]);
  const [period, setPeriod] = useState(todayKey()); // YYYY-MM
  const [scope, setScope] = useState("period");    // "period" | "all"

  const rows = useMemo(() => invoices.records.map((r) => {
    const f = r.fields || {};
    return {
      id: r.id,
      number: str(f["Invoice Number"]),
      client: linkedName(f["Client Link"], clientMap),
      service: str(f["Service"]),
      amount: money(f["Amount"]),
      fee: money(f["Processing Fee"]),
      net: money(f["Net Amount"]),
      date: f["Invoice Date"],
      rawDate: f["Date Paid"] || f["Invoice Date"] || "",
      status: f["Status"],
      method: f["Payment Method"],
      processor: f["Payment Processor"],
    };
  }), [invoices.records, clientMap]);

  const previous = prevKey(period);

  const visibleRows = useMemo(() => {
    if (scope === "all") return rows;
    return rows.filter((r) => monthOf(r.rawDate) === period);
  }, [rows, scope, period]);

  const totals = useMemo(() => {
    const subset = scope === "all" ? rows : rows.filter((r) => monthOf(r.rawDate) === period);
    const invoiced = subset.reduce((s, r) => s + r.amount, 0);
    const collected = subset.filter((r) => r.status === "Paid").reduce((s, r) => s + r.amount, 0);
    const pending = subset.filter((r) => r.status === "Pending").reduce((s, r) => s + r.amount, 0);
    const overdue = subset.filter((r) => r.status === "Overdue").reduce((s, r) => s + r.amount, 0);
    const avgPerClient = clients.records.length ? Math.round(collected / clients.records.length) : 0;
    return { invoiced, collected, pending, overdue, avgPerClient };
  }, [rows, scope, period, clients.records.length]);

  const previousTotals = useMemo(() => {
    if (scope === "all") return null;
    const subset = rows.filter((r) => monthOf(r.rawDate) === previous);
    return {
      invoiced: subset.reduce((s, r) => s + r.amount, 0),
      collected: subset.filter((r) => r.status === "Paid").reduce((s, r) => s + r.amount, 0),
      pending: subset.filter((r) => r.status === "Pending").reduce((s, r) => s + r.amount, 0),
      overdue: subset.filter((r) => r.status === "Overdue").reduce((s, r) => s + r.amount, 0),
    };
  }, [rows, scope, previous]);

  // 12-month chart ending at the selected period (or current month for all-time)
  const chartData = useMemo(() => {
    const endKey = scope === "all" ? todayKey() : period;
    const [ey, em] = endKey.split("-").map(Number);
    const months = [];
    for (let i = 11; i >= 0; i--) {
      let y = ey, m = em - i;
      while (m < 1) { m += 12; y -= 1; }
      months.push({ key: `${y}-${String(m).padStart(2, "0")}`, month: MN[m - 1] });
    }
    const map = Object.fromEntries(months.map((m) => [m.key, 0]));
    invoices.records.forEach((r) => {
      const f = r.fields || {};
      if (f["Status"] !== "Paid") return;
      const d = f["Date Paid"] || f["Invoice Date"];
      if (!d) return;
      const key = d.slice(0, 7);
      if (key in map) map[key] += Number(f["Amount"] || 0);
    });
    return months.map((m) => ({ month: m.month, revenue: map[m.key] }));
  }, [invoices.records, period, scope]);

  if (invoices.loading || clients.loading) return <Loading label="Loading revenue" />;
  if (invoices.error) return <ErrorState message={invoices.error} onRetry={invoices.refetch} />;

  return (
    <div data-testid="revenue-page">
      <PageHeader
        eyebrow="DASHBOARD / REVENUE"
        title="Revenue Breakdown"
        subtitle={
          scope === "all"
            ? `All-time view — ${rows.length} invoices on file.`
            : `Viewing ${periodLabel(period)} — comparing against ${periodLabel(previous)}.`
        }
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex border border-[rgba(42,28,21,0.18)] bg-[#E7DDCB]">
              <button
                data-testid="revenue-scope-period"
                onClick={() => setScope("period")}
                className={`px-3 py-1.5 font-body tracking-widest text-[0.75rem] uppercase ${scope === "period" ? "bg-[#2A1C15] text-[#DCCFB9]" : "text-[#2A1C15] hover:bg-[#2A1C15]/5"}`}
              >Month</button>
              <button
                data-testid="revenue-scope-all"
                onClick={() => setScope("all")}
                className={`px-3 py-1.5 font-body tracking-widest text-[0.75rem] uppercase border-l border-[rgba(42,28,21,0.18)] ${scope === "all" ? "bg-[#2A1C15] text-[#DCCFB9]" : "text-[#2A1C15] hover:bg-[#2A1C15]/5"}`}
              >All-Time</button>
            </div>
            {scope === "period" && <PeriodPicker value={period} onChange={setPeriod} />}
            <ExportButtons
              title={`Revenue — Invoices${scope === "period" ? " — " + periodLabel(period) : " — All-Time"}`}
              filename={`invoices${scope === "period" ? "-" + period : "-all-time"}`}
              testidPrefix="export-revenue"
              columns={[
                { key: "number", label: "Invoice #" },
                { key: "client", label: "Client" },
                { key: "service", label: "Service" },
                { key: "amount", label: "Amount" },
                { key: "fee", label: "Fee (3%)" },
                { key: "net", label: "Net" },
                { key: "date", label: "Date" },
                { key: "method", label: "Method" },
                { key: "processor", label: "Processor" },
                { key: "status", label: "Status" },
              ]}
              rows={visibleRows}
            />
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
        <div className="bj-card bj-card-hover p-5 flex flex-col gap-2 min-h-[120px]" data-testid="rev-invoiced">
          <div className="bj-label">Total Invoiced</div>
          <div className="bj-metric text-3xl sm:text-4xl text-[#2A1C15] bj-tnum">${totals.invoiced.toLocaleString()}</div>
          {previousTotals && <DeltaPill current={totals.invoiced} previous={previousTotals.invoiced} isCurrency />}
        </div>
        <div className="bj-card bj-card-hover p-5 flex flex-col gap-2 min-h-[120px]" data-testid="rev-collected">
          <div className="bj-label">Total Collected</div>
          <div className="bj-metric text-3xl sm:text-4xl text-[#14281E] bj-tnum">${totals.collected.toLocaleString()}</div>
          {previousTotals && <DeltaPill current={totals.collected} previous={previousTotals.collected} isCurrency />}
        </div>
        <div className="bj-card bj-card-hover p-5 flex flex-col gap-2 min-h-[120px]" data-testid="rev-pending">
          <div className="bj-label">Total Pending</div>
          <div className="bj-metric text-3xl sm:text-4xl text-[#6E5022] bj-tnum">${totals.pending.toLocaleString()}</div>
          {previousTotals && <DeltaPill current={totals.pending} previous={previousTotals.pending} isCurrency invertColors />}
        </div>
        <div className="bj-card bj-card-hover p-5 flex flex-col gap-2 min-h-[120px]" data-testid="rev-overdue">
          <div className="bj-label">Total Overdue</div>
          <div className="bj-metric text-3xl sm:text-4xl text-[#5C1A24] bj-tnum">${totals.overdue.toLocaleString()}</div>
          {previousTotals && <DeltaPill current={totals.overdue} previous={previousTotals.overdue} isCurrency invertColors />}
        </div>
        <div className="bj-card bj-card-hover p-5 flex flex-col gap-2 min-h-[120px]" data-testid="rev-avg">
          <div className="bj-label">Avg / Client</div>
          <div className="bj-metric text-3xl sm:text-4xl text-[#6E5022] bj-tnum">${totals.avgPerClient.toLocaleString()}</div>
          <div className="text-[0.75rem] text-[#5E4D40] tracking-widest uppercase">collected ÷ {clients.records.length} clients</div>
        </div>
      </div>

      <Card className="mb-8" data-testid="revenue-yearly-chart">
        <SectionTitle right={<span className="bj-label">Last 12 Months</span>}>Monthly Revenue — Full Year</SectionTitle>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="rgba(42,28,21,0.18)" vertical={false} />
              <XAxis dataKey="month" stroke="#5E4D40" tick={{ fontFamily: "Hanken Grotesk", fontSize: 11 }} axisLine={{ stroke: "#E7DDCB" }} tickLine={false} />
              <YAxis stroke="#5E4D40" tick={{ fontFamily: "Hanken Grotesk", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(200,164,106,0.06)" }} formatter={(v) => [`$${Number(v).toLocaleString()}`, "Revenue"]} />
              <Bar dataKey="revenue" fill="#6E5022" radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <SectionTitle right={<span className="bj-label">{visibleRows.length} record{visibleRows.length === 1 ? "" : "s"}</span>}>Invoices</SectionTitle>
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="text-left">
              {["Invoice #","Client","Service","Amount","Fee (3%)","Net","Date","Method","Processor","Status"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-[#5E4D40] text-sm">No invoices for {scope === "all" ? "this view" : periodLabel(period)}.</td></tr>}
            {visibleRows.map((r, i) => (
              <tr key={r.id} data-testid={`invoice-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-[#6E5022] bj-tnum text-sm">{r.number}</td>
                <td className="py-3 pr-3 text-[#2A1C15] text-sm">{r.client}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{relabel(r.service)}</td>
                <td className="py-3 pr-3 text-sm"><Money value={r.amount} tone="white" /></td>
                <td className="py-3 pr-3 text-sm"><Money value={r.fee} tone="red" /></td>
                <td className="py-3 pr-3 text-sm"><Money value={r.net} tone="green" /></td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(r.date)}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{str(r.method)}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{str(r.processor)}</td>
                <td className="py-3 pr-3">{r.status ? <StatusPill status={r.status} /> : <span className="text-[#5E4D40] text-sm">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default RevenuePage;
